// Workspace surface for a SAM communication draft (kind: 'draftcard'). Renders the editable
// ComposeCard where list cards render — the desktop side panel, the mobile card/UI mode, and (via
// HandsFreeView) the single hands-free page — so a draft behaves like every other workspace card.
// The card is editable; edits persist live and are surfaced to SAM. Mirrors UXView's outer shell
// (optional reply strip on mobile + a header with the "⋯" session menu). The header shows the
// channel ("Email" / "SMS" …) — reported by the card, so it also reflects send/discard.
import { useState } from 'react';
import ComposeCard, { type DraftCardMessage, type DraftSnapshot } from '@/components/Communications/ComposeCard';
import { HistoryMenu, ReplyStrip, type RecentEntry } from '@/components/chat/UXView';
import { WorkspaceHeader } from '@/components/UI/WorkspaceHeader';
import type { CommunicationChannel, CommunicationOut } from '@/redux/internal-services/workspaceApiExtensions';

const CHANNEL_LABEL: Record<CommunicationChannel, string> = { email: 'Email', sms: 'SMS', in_app: 'In-app' };
const STATUS_SUFFIX: Record<string, string> = { sent: ' · Sent', delivered: ' · Delivered', queued: ' · Sending', pending: ' · Sending', failed: ' · Failed', discarded: ' · Discarded' };

export const DraftPanel = ({
  draft, messageStrip, onExpandStrip, isWaiting, history = [], onReplay, historyHasMore, onHistoryLoadMore, onDiscard, onSent, onDraftEdit,
}: {
  draft: DraftCardMessage;
  /** SAM's reply for this turn, shown above the card on the mobile card surface. */
  messageStrip: string;
  onExpandStrip?: () => void;
  /** True while SAM is composing a new reply for this turn — shows the typing-dots strip. */
  isWaiting?: boolean;
  history?: RecentEntry[];
  onReplay?: (e: RecentEntry) => void;
  historyHasMore?: boolean;
  onHistoryLoadMore?: () => void;
  onDiscard?: () => void;
  onSent?: (comm: CommunicationOut) => void;
  onDraftEdit?: (snapshot: DraftSnapshot | null) => void;
}) => {
  const [meta, setMeta] = useState<{ channel: CommunicationChannel; status: string; isDraft: boolean } | null>(null);
  const channel = meta?.channel ?? draft.draft?.channel;
  // Channel name (never the generic "Draft"), with a status suffix once it's no longer a draft.
  const headerTitle = channel
    ? CHANNEL_LABEL[channel] + (meta && !meta.isDraft ? (STATUS_SUFFIX[meta.status] ?? '') : '')
    : 'Message';

  return (
    <div className="flex flex-col h-full min-h-0">
      <ReplyStrip text={messageStrip} onExpand={onExpandStrip} isWaiting={isWaiting} />
      <WorkspaceHeader
        title={headerTitle}
        actions={<HistoryMenu history={history} onReplay={onReplay} hasMore={historyHasMore} onLoadMore={onHistoryLoadMore} />}
      />
      <div className="flex-1 overflow-y-auto no-scrollbar px-3 py-3 min-h-0">
        <ComposeCard
          key={draft.communication_id ?? draft.id}
          communicationId={draft.communication_id}
          draft={draft.draft}
          seedRev={draft.rev}
          onDiscard={onDiscard}
          onSent={onSent}
          onDraftEdit={onDraftEdit}
          onMeta={setMeta}
        />
      </div>
    </div>
  );
};

export default DraftPanel;
